import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  MapPin,
  RotateCcw,
  Search,
  SlidersHorizontal,
  Store,
  X,
} from 'lucide-react';
import type { HierarchyChip } from '@/features/billboards/types/hierarchy';
import {
  findLocationTypes,
  findMidOptions,
  getMergedMidOptions,
  getMergedSubOptions,
  getMidLabel,
  getSubLabel,
  toChip,
  type VenueHierarchy,
} from '@/features/billboards/types/hierarchy';
import {
  LocalityRadiusField,
  type LocalityPlace,
} from '@/features/billboards/components/list/LocalityRadiusField';
import { NEARBY_DEFAULT_RADIUS } from '@/features/billboards/utils/nearbyBillboardsUtils';
import { env } from '@/shared/config/env';
import {
  prefetchGooglePlaces,
  resolveCityBounds,
} from '@/shared/lib/maps/placesAutocomplete';
import {
  EMPTY_SCREEN_FILTERS,
  type ScreenFilters,
} from '../ScreensStep.types';
import {
  nextScreenFilters,
  pruneScreenFilters,
} from '../screensStepHelpers';
import { VenueFilterSelect } from './VenueFilterSelect';

export type LocationVenueFilterDraft = {
  localityPlace: LocalityPlace;
  areaQuery: string;
  filters: ScreenFilters;
};

type LocationVenueFilterModalProps = {
  open: boolean;
  onClose: () => void;
  onApply: (draft: LocationVenueFilterDraft) => void;
  localityPlace: LocalityPlace;
  city?: string;
  areaQuery: string;
  filters: ScreenFilters;
  activeFilterCount: number;
  locationTypes: HierarchyChip[];
  hierarchy?: VenueHierarchy;
  venuesLoading: boolean;
};

const EMPTY_LOCALITY: LocalityPlace = {
  label: '',
  lat: null,
  lng: null,
  radius: NEARBY_DEFAULT_RADIUS,
};

export function LocationVenueFilterModal({
  open,
  onClose,
  onApply,
  localityPlace,
  city,
  areaQuery,
  filters,
  activeFilterCount,
  locationTypes,
  hierarchy,
  venuesLoading,
}: LocationVenueFilterModalProps) {
  const [draftLocality, setDraftLocality] =
    useState<LocalityPlace>(localityPlace);
  const [draftArea, setDraftArea] = useState(areaQuery);
  const [draftFilters, setDraftFilters] = useState<ScreenFilters>(filters);

  useEffect(() => {
    if (!open) return;
    setDraftLocality(localityPlace);
    setDraftArea(areaQuery);
    setDraftFilters(filters);
  }, [open, localityPlace, areaQuery, filters]);

  // Warm Maps/Places + city viewport when the sheet opens (no prediction charge)
  useEffect(() => {
    if (!open || !env.GOOGLE_LOCATION_KEY) return;
    prefetchGooglePlaces(env.GOOGLE_LOCATION_KEY);
    if (city) {
      void resolveCityBounds(env.GOOGLE_LOCATION_KEY, city).catch(() => {
        /* ignore warm-up errors */
      });
    }
  }, [open, city]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  const selectedLocations = useMemo(
    () => findLocationTypes(hierarchy, draftFilters.locationType),
    [hierarchy, draftFilters.locationType],
  );
  const midOptions = useMemo(
    () => getMergedMidOptions(selectedLocations).map(toChip),
    [selectedLocations],
  );
  const selectedMids = useMemo(
    () => findMidOptions(selectedLocations, draftFilters.venueType),
    [selectedLocations, draftFilters.venueType],
  );
  const subOptions = useMemo(
    () => getMergedSubOptions(selectedMids).map(toChip),
    [selectedMids],
  );
  const midLabel = getMidLabel(selectedLocations[0]) || 'Venue types';
  const subLabel = getSubLabel(selectedMids[0]) || 'Sub venue types';

  const hasLocalityPin =
    Boolean(draftLocality.label?.trim()) ||
    (Number.isFinite(draftLocality.lat) && Number.isFinite(draftLocality.lng));

  const draftActiveCount = useMemo(() => {
    return (
      Number(draftFilters.locationType.length > 0) +
      Number(draftFilters.venueType.length > 0) +
      Number(draftFilters.venueSubType.length > 0) +
      Number(hasLocalityPin) +
      Number(Boolean(draftArea.trim()))
    );
  }, [draftFilters, hasLocalityPin, draftArea]);

  const updateDraftFilter = (key: keyof ScreenFilters, value: string[]) => {
    setDraftFilters((prev) => {
      const next = nextScreenFilters(prev, key, value);
      const locations = findLocationTypes(hierarchy, next.locationType);
      const mids = getMergedMidOptions(locations);
      const selected = findMidOptions(locations, next.venueType);
      const subs = getMergedSubOptions(selected);
      return pruneScreenFilters(
        next,
        mids.flatMap((item) => [item.key, item.label]),
        subs.flatMap((item) => [item.key, item.label]),
      );
    });
  };

  const resetDraft = () => {
    setDraftLocality(EMPTY_LOCALITY);
    setDraftArea('');
    setDraftFilters(EMPTY_SCREEN_FILTERS);
  };

  const handleApply = () => {
    onApply({
      localityPlace: draftLocality,
      areaQuery: draftArea,
      filters: draftFilters,
    });
    onClose();
  };

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-modal-title"
    >
      <button
        type="button"
        aria-label="Close filters"
        className="absolute inset-0 border-0 bg-black/40 backdrop-blur-[3px] outline-none"
        onClick={onClose}
      />

      <div
        className="relative z-10 flex max-h-[94vh] w-full max-w-[440px] flex-col overflow-hidden rounded-t-[24px] bg-[#F2F4F7] shadow-[0_28px_80px_-20px_rgba(16,38,73,0.5)] sm:max-h-[88vh] sm:rounded-[24px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Grabber (iOS sheet cue) */}
        <div className="flex justify-center pt-2.5 sm:hidden" aria-hidden>
          <span className="h-1 w-9 rounded-full bg-black/15" />
        </div>

        <header className="flex shrink-0 items-start justify-between gap-3 px-5 pb-3 pt-3 sm:pt-5">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-white text-[var(--dash-brand)] shadow-[0_1px_3px_rgba(16,38,73,0.08)]">
              <SlidersHorizontal className="h-4 w-4" />
            </span>
            <div className="min-w-0 text-left">
              <h2
                id="filter-modal-title"
                className="m-0 text-[17px] font-semibold tracking-[-0.025em] text-[var(--dash-ink)]"
              >
                Filters
              </h2>
              <p className="m-0 mt-0.5 text-[12px] text-[var(--dash-muted)]">
                Place, area, and venue — apply when ready
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.06] text-[var(--dash-ink)] transition hover:bg-black/[0.1]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 pb-4 text-left sm:px-5">
          {/* Group 1 — Location */}
          <section className="overflow-hidden rounded-[18px] bg-white shadow-[0_1px_2px_rgba(16,38,73,0.04)]">
            <div className="flex items-center gap-2 border-b border-black/[0.04] px-4 py-3">
              <MapPin className="h-3.5 w-3.5 text-[var(--dash-brand)]" />
              <h3 className="m-0 text-[13px] font-semibold tracking-[-0.015em] text-[var(--dash-ink)]">
                Near a place
              </h3>
            </div>
            <div className="px-4 py-3.5">
              <LocalityRadiusField
                value={draftLocality}
                city={city}
                onChange={setDraftLocality}
                variant="sheet"
              />
            </div>
          </section>

          {/* Group 2 — Area keyword */}
          <section className="overflow-hidden rounded-[18px] bg-white shadow-[0_1px_2px_rgba(16,38,73,0.04)]">
            <div className="flex items-center gap-2 border-b border-black/[0.04] px-4 py-3">
              <Search className="h-3.5 w-3.5 text-[var(--dash-brand)]" />
              <h3 className="m-0 text-[13px] font-semibold tracking-[-0.015em] text-[var(--dash-ink)]">
                Area keyword
              </h3>
            </div>
            <div className="px-4 py-3.5">
              <input
                value={draftArea}
                onChange={(e) => setDraftArea(e.target.value)}
                placeholder="Kolar Road, MP Nagar, Arera Colony…"
                className="h-11 w-full rounded-[14px] border border-black/[0.08] bg-[#F7F8FA] px-3.5 text-[13px] text-[var(--dash-ink)] outline-none transition placeholder:text-[#8E98A8] focus:border-[var(--dash-brand)]/40 focus:bg-white focus:shadow-[0_0_0_3px_rgba(41,121,255,0.12)]"
                aria-label="Area keyword"
              />
              <p className="m-0 mt-2 text-[11px] leading-snug text-[var(--dash-muted)]">
                Matches screens whose address or area contains this text.
              </p>
            </div>
          </section>

          {/* Group 3 — Venue cascade (stacked for full labels) */}
          <section className="overflow-hidden rounded-[18px] bg-white shadow-[0_1px_2px_rgba(16,38,73,0.04)]">
            <div className="flex items-center gap-2 border-b border-black/[0.04] px-4 py-3">
              <Store className="h-3.5 w-3.5 text-[var(--dash-brand)]" />
              <h3 className="m-0 text-[13px] font-semibold tracking-[-0.015em] text-[var(--dash-ink)]">
                Venue type
              </h3>
            </div>
            <div className="space-y-4 px-4 py-3.5">
              <VenueFilterSelect
                step={1}
                label="Location type"
                loading={venuesLoading && !locationTypes.length}
                options={locationTypes}
                value={draftFilters.locationType}
                onChange={(value) => updateDraftFilter('locationType', value)}
                placeholder="Indoor, outdoor, transit…"
              />
              <VenueFilterSelect
                step={2}
                label={midLabel}
                options={midOptions}
                value={draftFilters.venueType}
                onChange={(value) => updateDraftFilter('venueType', value)}
                placeholder={
                  draftFilters.locationType.length
                    ? `Choose ${midLabel.toLowerCase()}`
                    : 'Select location type first'
                }
                disabled={!draftFilters.locationType.length}
                hint={
                  draftFilters.locationType.length
                    ? undefined
                    : 'Step 1 unlocks venue categories'
                }
              />
              <VenueFilterSelect
                step={3}
                label={subLabel}
                options={subOptions}
                value={draftFilters.venueSubType}
                onChange={(value) => updateDraftFilter('venueSubType', value)}
                placeholder={
                  draftFilters.venueType.length
                    ? `Choose ${subLabel.toLowerCase()}`
                    : 'Select venue type first'
                }
                disabled={!draftFilters.venueType.length}
                hint={
                  draftFilters.venueType.length
                    ? undefined
                    : 'Step 2 unlocks sub-types'
                }
              />
            </div>
          </section>
        </div>

        <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-black/[0.06] bg-white/90 px-4 py-3.5 backdrop-blur-md sm:px-5">
          {draftActiveCount > 0 || activeFilterCount > 0 ? (
            <button
              type="button"
              onClick={resetDraft}
              className="inline-flex items-center gap-1.5 rounded-full px-2 py-1.5 text-[12px] font-semibold text-[var(--dash-muted)] transition hover:bg-rose-50 hover:text-rose-600"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Clear
            </button>
          ) : (
            <span className="text-[12px] text-[var(--dash-muted)]">
              No filters yet
            </span>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full px-4 py-2 text-[13px] font-semibold text-[var(--dash-ink)] transition hover:bg-black/[0.04]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--dash-brand)] px-5 py-2.5 text-[13px] font-semibold text-white shadow-[0_4px_16px_-4px_rgba(41,121,255,0.55)] transition hover:opacity-95 active:scale-[0.98]"
            >
              <Check className="h-3.5 w-3.5 stroke-[2.5]" />
              Apply
              {draftActiveCount > 0 ? (
                <span className="rounded-full bg-white/20 px-1.5 py-px text-[11px] tabular-nums">
                  {draftActiveCount}
                </span>
              ) : null}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
